import { DateValue, getLocalTimeZone, today } from "@internationalized/date";
import { format } from "date-fns";
import { useOplogg } from "features/TodoOploggSidebar/Oplogg/useOplogg";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropsDatePicker,
  Flex,
  SearchInput,
  Separator,
  TimePicker,
} from "shared/components/Spor";
import { TextTooltip } from "shared/components/TextTooltip";
import styled, { useTheme } from "styled-components";

/**
 * Necessary in order to hide time display (show --:--). This is because if
 * you try to hide the time (aka time == null) with Spor's TimePicker, it
 * disables(!) the TimePicker completely, which renders it unusable.
 */
const TimePickerHolder = styled(Flex)<{ time: string | null }>`
  &&& {
    /* Necessary in order for the TimePicker to take the necessary space when time != null  */
    flex-grow: 1;
    time {
      width: 100%;
    }
    .spor-field__root > * {
      width: 100%;
    }
  }
  ${({ time, theme }) =>
    !time &&
    `
  div[role="spinbutton"] {
    position: relative;
    color: transparent; /* hide existing content */
    margin: 0px 10px 0px 2px;
  }

  div[role="spinbutton"]::after {
    content: "––";
    position: absolute;
    left: 0;
    font-size: 18px;
    color: ${theme.colorTextMain};
  }
  `};
`;

export const OploggSearch: FC = () => {
  const { setSelectedDateTime, setSearchString, searchString } = useOplogg();
  const theme = useTheme();
  const { t } = useTranslation();

  const [date, setDate] = useState<DateValue | null>(null);
  const [time, setTime] = useState<string | null>(null);

  // change date when you pass midnight
  const handleChangeTime = useCallback(
    (newTime: string | undefined) => {
      if (searchString) setTime(null);

      const selectedTime = time ? time.slice(0, 5) : "00:00";
      const newTimeFormatted = newTime ? newTime.slice(0, 5) : "00:00";

      if (date && /^00:/.test(selectedTime) && /^23:/.test(newTimeFormatted)) {
        setDate(date.subtract({ days: 1 }));
      } else if (
        date &&
        /^23:/.test(selectedTime) &&
        /^00:/.test(newTimeFormatted)
      ) {
        setDate(date.add({ days: 1 }));
      }

      setTime(newTimeFormatted);
    },
    [searchString, time, date, setTime, setDate],
  );

  useEffect(() => {
    if (time && date) {
      setSelectedDateTime(
        `${format(date.toDate(getLocalTimeZone()), "yyyy-MM-dd")}T${time}:00`,
      );
    } else if (date) {
      setSelectedDateTime(
        format(date.toDate(getLocalTimeZone()), "yyyy-MM-dd"),
      );
    } else if (time) {
      const todaysDate = today(getLocalTimeZone());
      setDate(todaysDate);
    }
  }, [date, time]);

  useEffect(() => {
    if (searchString) {
      setTime(null);
    }
  }, [searchString]);
  return (
    <>
      <Separator
        mx={-2}
        w="calc(100% + 24px)"
        borderTop={`1px solid ${theme.colorOutlineSecondary}`}
      />
      <SearchInput
        label={t("Søk etter strekning, type hendelse...")}
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
        width="calc(100% + 4px)"
        mx="-2px"
      />
      <Flex gap="2">
        <TimePickerHolder time={time}>
          <TextTooltip
            label={
              searchString
                ? t("Tøm søkefeltet for å bruke tidsvelgeren")
                : undefined
            }
            showArrow
          >
            <TimePicker
              disabled={!!searchString}
              label={t("Tidspunkt")}
              minuteInterval={60}
              onValueChange={(newTime) => handleChangeTime(newTime?.toString())}
            />
          </TextTooltip>
        </TimePickerHolder>
        <DropsDatePicker
          label={t("Dato")}
          variant="core"
          value={date}
          width="calc(50% - 6px)"
          minValue={today(getLocalTimeZone()).subtract({ days: 7 })}
          maxValue={today(getLocalTimeZone()).add({ days: 1 })}
          onValueChange={(value) => {
            setDate(value ?? today(getLocalTimeZone()));
          }}
          showYearNavigation={false}
          withPortal
        />
      </Flex>
    </>
  );
};
